/**
 * Delivery state of the inspection report emails (#3787): one history per
 * report that has been sent, since the email carries the report's id.
 *
 * A report row keeps only its latest channel, so a report re-sent by WhatsApp
 * no longer says "email". Its earlier email history stays reachable: the
 * history is read by report id, and when there is none it says so rather than
 * implying an email was sent. Reports never sent are left out.
 */
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { EmailDeliveryHistory } from '@/components/email/EmailDeliveryHistory';
import { useRevealedCount } from '@/components/email/useRevealedCount';
import { useInspectionReports } from '@/hooks/useInspections';
import { formatDateTime } from '@/utils/locale';

export function InspectionReportDeliveries({ inspectionId }: { inspectionId: string | undefined }) {
  const { t } = useTranslation('common');
  const reports = useInspectionReports(inspectionId);
  const sent = (reports.data ?? []).filter((report) => report.sentVia !== null || report.sentAt !== null);
  const { visible, hasMore, showMore } = useRevealedCount(inspectionId, sent.length);

  if (reports.isError) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void reports.refetch()}>
            {t('emailDelivery.retry')}
          </Button>
        }
      >
        {t('emailDelivery.reportsLoadError')}
      </Alert>
    );
  }
  if (reports.isPending && reports.fetchStatus !== 'idle') {
    return (
      <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={16} aria-hidden="true" />
        <Typography variant="body2">{t('emailDelivery.loading')}</Typography>
      </Box>
    );
  }
  if (sent.length === 0) return null;
  return (
    <Stack spacing={2} sx={{ mt: 2 }} data-testid="inspection-report-deliveries">
      {sent.slice(0, visible).map((report) => (
        <div key={report.id} data-testid="inspection-report-delivery">
          <Typography variant="caption" color="text.secondary" component="p">
            {t('emailDelivery.reportCaption', { time: formatDateTime(report.sentAt ?? report.createdAt) })}
            {report.sentVia === 'email' ? '' : ` · ${t('emailDelivery.reportNotEmail')}`}
          </Typography>
          <EmailDeliveryHistory
            entityType="inspection_report"
            entityId={report.id}
            title={t('emailDelivery.inspectionReportTitle')}
          />
        </div>
      ))}
      {hasMore ? (
        <Box>
          <Button size="small" onClick={showMore}>
            {t('emailDelivery.showMoreReports')}
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
}
